export const ambassadorStatuses: Record<string, string> = {
  RECRUITING: "در حال جذب",
  ONBOARDING: "در حال راه‌اندازی",
  ACTIVE: "فعال",
  NEEDS_SUPPORT: "نیازمند پشتیبانی",
  PAUSED: "موقتاً غیرفعال",
  WITHDRAWN: "انصراف‌داده",
  DECLINED: "رد همکاری",
};
export const ambassadorRoles: Record<string, string> = {
  RECRUITER: "مسئول جذب",
  SUPPORT: "پشتیبان",
  CONTENT: "مسئول محتوا",
  SUPERVISOR: "سرپرست",
};
export const materialKinds: Record<string, string> = {
  TRAINING: "آموزش",
  CONTENT: "محتوا",
  ANNOUNCEMENT: "اطلاعیه",
  REFERENCE: "کانال مرجع",
  TEMPLATE: "متن آماده",
};
export interface AmbassadorPerson {
  id: string;
  firstName?: string;
  lastName?: string;
  username: string;
}
export interface AmbassadorContext {
  roles: string[];
  profile: { id: string; status: string } | null;
  onboarding: { key: string; title: string; required: boolean }[];
  daily: { key: string; title: string }[];
  supportDaily: string[];
  consent: string;
}
export interface AmbassadorChecklist {
  key: string;
  completed: boolean;
  completedAt?: string;
  completedBy?: string;
}
export interface AmbassadorReport {
  id: string;
  day: string;
  summary: string;
  publishedCount: number;
  meaningfulInteractions: number;
  dailyChecklist: string[];
  mission?: { title: string };
  evidenceUrl?: string;
  feedback?: string;
  quality?: number;
}
export interface AmbassadorTicket {
  id: string;
  subject: string;
  resolvedAt?: string;
  replies: { id: string; actorId: string; body: string; createdAt: string }[];
}
export interface AmbassadorProfile {
  id: string;
  fullName: string;
  userId?: string;
  user?: AmbassadorPerson;
  phone?: string;
  telegramUsername?: string;
  source?: string;
  supportId?: string;
  support?: AmbassadorPerson;
  status: string;
  channelUrl?: string;
  memberCount: number;
  acceptedAt?: string;
  lastActivityAt?: string;
  nextFollowUpAt?: string;
  activatedAt?: string;
  checklist: AmbassadorChecklist[];
  events?: {
    id: string;
    actorId: string;
    kind: string;
    note: string;
    createdAt: string;
  }[];
  reports?: AmbassadorReport[];
  tickets?: AmbassadorTicket[];
  weeklyReviews?: AmbassadorWeeklyReview[];
  historyCounts?: Partial<Record<AmbassadorHistoryKind, number>>;
}
export type AmbassadorHistoryKind =
  | "reports"
  | "tickets"
  | "events"
  | "weeklyReviews";
export interface AmbassadorWeeklyReview {
  id: string;
  weekStart: string;
  decision: string;
  summary: string;
  nextAction: string;
  reviewedBy: string;
  updatedAt: string;
}
export const weeklyDecisions: Record<string, string> = {
  CONTINUE: "ادامه فعالیت",
  TRAINING: "آموزش مجدد",
  SUPPORT: "پیگیری و رفع مانع",
  PAUSE: "پیشنهاد توقف موقت",
};
export const ambassadorEventLabels: Record<string, string> = {
  APPLICATION_APPROVED: "تأیید درخواست عضویت",
  CREATED: "ایجاد پرونده",
  UPDATED: "ویرایش پرونده",
  SELF_UPDATED: "به‌روزرسانی کانال",
  CONSENT_ACCEPTED: "پذیرش همکاری",
  CONSENT_DECLINED: "عدم تمایل به همکاری",
  CHECKLIST: "به‌روزرسانی مراحل ورود",
  STATUS_CHANGED: "تغییر وضعیت",
  NOTE: "یادداشت داخلی",
  CALL_SUCCESS: "تماس موفق",
  CALL_NO_ANSWER: "بدون پاسخ",
  CALL_FAILED: "تماس ناموفق",
  SUPPORT_DAILY: "پیگیری پشتیبان",
  REPORT_CREATED: "ثبت گزارش فعالیت",
  REPORT_REVIEWED: "ارزیابی گزارش",
  TICKET_CREATED: "درخواست پشتیبانی",
  TICKET_UPDATED: "پیگیری درخواست",
  WEEKLY_REVIEWED: "ارزیابی هفتگی",
};
export const ambassadorDay = (day?: string) =>
  day
    ? new Date(`${day}T12:00:00+03:30`).toLocaleDateString("fa-IR", {
        timeZone: "Asia/Tehran",
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "—";
export interface AmbassadorMaterial {
  id: string;
  kind: string;
  title: string;
  body: string;
  url?: string;
  published: boolean;
}
export interface AmbassadorMission {
  id: string;
  title: string;
  objective: string;
  action: string;
  url?: string;
  deadline?: string;
  published: boolean;
}
export interface AmbassadorResources {
  materials: AmbassadorMaterial[];
  missions: AmbassadorMission[];
}
export interface AmbassadorStaff {
  userId: string;
  roles: string[];
  user: AmbassadorPerson;
}
export type AmbassadorApplicationStatus = "PENDING" | "APPROVED" | "REJECTED";
export const ambassadorApplicationStatuses: Record<
  AmbassadorApplicationStatus,
  string
> = {
  PENDING: "در انتظار بررسی",
  APPROVED: "تأییدشده",
  REJECTED: "پذیرفته‌نشده",
};
export const ambassadorWeeklyHours = {
  UP_TO_3: "تا ۳ ساعت در هفته",
  THREE_TO_SEVEN: "۳ تا ۷ ساعت در هفته",
  OVER_SEVEN: "بیش از ۷ ساعت در هفته",
};
export interface AmbassadorApplication {
  id: string;
  userId: string;
  fullName: string;
  phone: string;
  city: string;
  occupation?: string | null;
  telegramUsername?: string | null;
  weeklyHours: keyof typeof ambassadorWeeklyHours;
  motivation: string;
  experience?: string | null;
  consentAt: string;
  status: AmbassadorApplicationStatus;
  reviewNote?: string | null;
  reviewedAt?: string | null;
  reviewedBy?: string | null;
  profileId?: string | null;
  createdAt: string;
  updatedAt: string;
  user?: AmbassadorPerson;
}
export interface AmbassadorDashboard {
  total: number;
  statuses: Record<string, number>;
  followUps: AmbassadorProfile[];
  incomplete: AmbassadorProfile[];
  openTickets: number;
  supportChecklist?: { items: string[]; note: string };
  weekly: {
    acceptanceRate: number | null;
    onboardingRate: number | null;
    continuityRate: number | null;
    publishedCount: number;
    meaningfulInteractions: number;
    supportRate: number | null;
    averageQuality: number | null;
    reviewedReports: number;
  };
  definitions: string;
}
export const ambassadorDate = (value?: string) =>
  value
    ? new Date(value).toLocaleString("fa-IR", { timeZone: "Asia/Tehran" })
    : "—";
export const ambassadorPerson = (person?: AmbassadorPerson) =>
  person
    ? [person.firstName, person.lastName].filter(Boolean).join(" ") ||
      person.username
    : "تعیین نشده";
export const ambassadorError = (error: unknown) => {
  const data = (
    error as { response?: { data?: { message?: string | string[] } } }
  )?.response?.data;
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  if (!status)
    return "ارتباط با سرور برقرار نشد. اتصال اینترنت را بررسی کنید؛ اطلاعات فرم حفظ شده است.";
  if (status === 401) return "نشست شما پایان یافته است. دوباره وارد حساب شوید.";
  if (status === 403)
    return "اجازه انجام این کار را ندارید. با سرپرست سفیران هماهنگ کنید.";
  if (status === 429)
    return "تعداد درخواست‌ها زیاد شده است. کمی صبر کنید و دوباره تلاش کنید.";
  if (Array.isArray(data?.message))
    return "اطلاعات فرم معتبر نیست. فیلدهای الزامی، تعدادها و لینک‌های https را بررسی کنید.";
  return data?.message || "انجام درخواست ممکن نشد. دوباره تلاش کنید.";
};
